import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Copy, Trash } from "@phosphor-icons/react";
import client from "@/lib/api";

const PLAN_NAME = { pro: "Pro", boss: "Boss Up" };
const errMsg = (err) => err?.response?.data?.detail || "Something went wrong";

export const PromoRedeem = ({ user, refresh }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const redeem = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Enter a promo code");
    setBusy(true);
    try {
      const { data } = await client.post("/promo/redeem", { code });
      toast.success(`${PLAN_NAME[data.plan]} unlocked free for ${data.duration_label}`);
      setCode("");
      refresh?.();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="border border-[#22C55E]/40 bg-[#22C55E]/5 p-5 mb-6" data-testid="promo-redeem-panel">
      <div className="text-xs font-mono-k tracking-widest-k text-[#22C55E] mb-1 flex items-center gap-2"><Ticket size={14} /> — HAVE A PROMO CODE?</div>
      {user?.promo_expires_at && (
        <div className="text-sm font-mono-k mb-3" data-testid="promo-active-status">
          Free {PLAN_NAME[user.plan] || user.plan} access active until <span className="text-[#22C55E]">{user.promo_expires_at.slice(0, 10)}</span>. You'll move back to Free after that.
        </div>
      )}
      <form onSubmit={redeem} className="flex gap-2 flex-wrap">
        <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="ENTER CODE" className="k-input flex-1 min-w-[200px] font-mono-k tracking-widest-k" data-testid="promo-code-input" />
        <button disabled={busy} className="k-btn primary text-xs" data-testid="promo-redeem-btn">{busy ? "CHECKING…" : "REDEEM"}</button>
      </form>
    </div>
  );
};

const PromoForm = ({ onCreated }) => {
  const [f, setF] = useState({ code: "", plan: "pro", duration: "1m", max_uses: 1, note: "" });
  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await client.post("/admin/promo-codes", { ...f, max_uses: parseInt(f.max_uses, 10) || 1 });
      toast.success(`Created ${data.code}`);
      setF({ ...f, code: "", note: "" });
      onCreated();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={create} className="border border-white/10 bg-[#111318] p-5 mb-6 grid md:grid-cols-5 gap-3 items-end" data-testid="promo-create-form">
      <div><label className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA]">CODE (blank = random)</label><input value={f.code} onChange={e => setF({ ...f, code: e.target.value.toUpperCase() })} className="k-input mt-1" placeholder="SUMMER2026" data-testid="promo-new-code" /></div>
      <div><label className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA]">PLAN</label>
        <select value={f.plan} onChange={e => setF({ ...f, plan: e.target.value })} className="k-input mt-1" data-testid="promo-new-plan"><option value="pro">Pro</option><option value="boss">Boss Up</option></select></div>
      <div><label className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA]">FREE FOR</label>
        <select value={f.duration} onChange={e => setF({ ...f, duration: e.target.value })} className="k-input mt-1" data-testid="promo-new-duration"><option value="1m">1 month</option><option value="3m">3 months</option><option value="1y">1 year</option></select></div>
      <div><label className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA]">USAGE LIMIT</label><input type="number" min="1" value={f.max_uses} onChange={e => setF({ ...f, max_uses: e.target.value })} className="k-input mt-1" data-testid="promo-new-max" /></div>
      <button className="k-btn primary text-xs justify-center" data-testid="promo-create-btn">CREATE CODE</button>
      <input value={f.note} onChange={e => setF({ ...f, note: e.target.value })} className="k-input md:col-span-5" placeholder="Internal note (optional) — e.g. who it's for" data-testid="promo-new-note" />
    </form>
  );
};

const PromoRow = ({ p, reload }) => {
  const toggle = async () => {
    try { await client.put(`/admin/promo-codes/${p.id}`, { active: !p.active }); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${p.code}? People who already redeemed it keep their free period.`)) return;
    await client.delete(`/admin/promo-codes/${p.id}`); reload();
  };
  const full = p.uses >= p.max_uses;
  return (
    <div className="border-b border-white/10 p-4 flex items-center gap-3 flex-wrap" data-testid={`promo-row-${p.code}`}>
      <div className="flex-1 min-w-[220px]">
        <div className="font-mono-k text-lg text-[#FACC15] tracking-widest-k">{p.code}</div>
        <div className="text-xs font-mono-k text-[#A1A1AA]">{PLAN_NAME[p.plan]} · free for {p.duration_label}{p.note ? ` · ${p.note}` : ""}</div>
      </div>
      <div className="text-sm font-mono-k" data-testid={`promo-uses-${p.code}`}>{p.uses} / {p.max_uses} USED</div>
      <div className={`text-[10px] font-mono-k tracking-widest-k px-2 py-1 border ${!p.active ? "border-red-500 text-red-400" : full ? "border-white/30 text-[#A1A1AA]" : "border-[#22C55E] text-[#22C55E]"}`}>{!p.active ? "PAUSED" : full ? "USED UP" : "ACTIVE"}</div>
      <button onClick={() => { Promise.resolve().then(() => navigator.clipboard.writeText(p.code)).then(() => toast.success("Code copied")).catch(() => toast.error("Couldn't copy — select the code manually")); }} className="k-btn text-xs" data-testid={`promo-copy-${p.code}`}><Copy size={12} /></button>
      <button onClick={toggle} className="k-btn text-xs" data-testid={`promo-toggle-${p.code}`}>{p.active ? "PAUSE" : "RESUME"}</button>
      <button onClick={del} className="k-btn text-xs" data-testid={`promo-del-${p.code}`}><Trash size={12} /></button>
    </div>
  );
};

export const AdminPromoCodes = () => {
  const [items, setItems] = useState([]);
  const load = () => client.get("/admin/promo-codes").then(r => setItems(r.data)).catch(err => toast.error(errMsg(err)));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <div className="mb-8">
        <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-2">— ADMIN · PROMO CODES</div>
        <h1 className="font-display text-4xl tracking-tighter">Give paid plans away, on your terms.</h1>
      </div>
      <PromoForm onCreated={load} />
      <div className="border border-white/10" data-testid="promo-list">
        {items.length === 0 && <div className="p-6 text-[#A1A1AA] font-mono-k text-sm">No promo codes yet.</div>}
        {items.map(p => <PromoRow key={p.id} p={p} reload={load} />)}
      </div>
    </div>
  );
};
